import BodyText from '@lyra/core/components/BodyText'
import Table, { createColumnHelper, TableData, TableProps } from '@lyra/core/components/Table'
import TooltipText from '@lyra/core/components/TooltipText'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import { MARKET_ICON_SIZE } from '@lyra/web/constants/layout'
import { PageId } from '@lyra/web/constants/pages'
import { SubaccountData } from '@lyra/web/constants/subaccount'
import { collateralConfig, CollateralId } from '@lyra/web/constants/tokens'
import useCollateralApys from '@lyra/web/hooks/useCollateralApys'
import useCollateralData from '@lyra/web/hooks/useCollateralData'
import { getPagePath } from '@lyra/web/utils/pages'
import {
  formatCollateralApy,
  formatCollateralBalance,
  formatTokenName,
  formatTokenSymbol,
  getActiveCollateral,
  getDefaultDepositTokenForToken,
  getTokenForCollateral,
  sortCollaterals,
} from '@lyra/web/utils/tokens'
import { useRouter } from 'next/navigation'
import React, { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import TokenIcon from '../../components/common/TokenIcon'
import DepositButton from './DepositButton'
import TransferButton from './TransferButton'

type BalancesTableData = {
  subaccountId?: number
  collateralId: CollateralId
  amount: number
  value: number
  margin: number
  apy: number
}

type Props = {
  subaccount?: SubaccountData | SubaccountData[]
  showActions?: boolean
  showApy?: boolean
} & Omit<TableProps<BalancesTableData>, 'columns' | 'data'>

const BalancesTable = ({ subaccount, showActions, showApy, ...tableProps }: Props): JSX.Element => {
  const subaccounts = useMemo(
    () => (subaccount ? (Array.isArray(subaccount) ? subaccount : [subaccount]) : []),
    [subaccount]
  )

  const { data: collateralApys } = useCollateralApys()
  const { data: usdcData } = useCollateralData(CollateralId.USDC)

  const borrowApy = usdcData?.borrow_apy ?? 0

  const router = useRouter()

  const rows: TableData<BalancesTableData>[] = useMemo(() => {
    return sortCollaterals(getActiveCollateral())
      .map((collateralId) => ({
        collateralId,
        amount: 0,
        value: 0,
        margin: 0,
        composition: 0,
        apy: collateralApys ? collateralApys[collateralId] ?? 0 : 0,
        onPress: () => router.push(getPagePath({ page: PageId.Collateral, collateralId })),
      }))
      .map((collateralObj: BalancesTableData) => {
        for (const subaccount of subaccounts) {
          const subCollateral = subaccount.subaccount.collaterals.find(
            (collat) => collat.asset_name === collateralObj.collateralId
          )

          if (!subCollateral) {
            continue
          }

          const margin = +subCollateral.initial_margin
          const markValue = +subCollateral.mark_value

          collateralObj.amount += +subCollateral.amount
          collateralObj.value += markValue
          collateralObj.margin += margin

          if (subaccounts.length === 1) {
            // only specify subaccount ID when there is 1 subaccount provided
            collateralObj.subaccountId = subaccount.subaccount.subaccount_id
          }
        }

        return collateralObj
      })
      .filter((collat) => collat.amount !== 0)
      .sort((a, b) => b.value - a.value)
  }, [collateralApys, subaccounts, router])

  const columnHelper = createColumnHelper<BalancesTableData>()

  const columns = useMemo(
    () =>
      filterNulls([
        columnHelper.accessor('collateral', {
          id: 'collateral',
          header: 'Collateral',
          meta: {
            minWidth: 125,
          },
          cell: (props) => {
            const { collateralId } = props.row.original
            return (
              <XStack gap="$1" alignItems="center">
                <TokenIcon size={MARKET_ICON_SIZE} symbol={collateralId} />
                <YStack>
                  <BodyText>{formatTokenSymbol(collateralId)}</BodyText>
                  <BodyText color="secondary">{formatTokenName(collateralId)}</BodyText>
                </YStack>
              </XStack>
            )
          },
        }),
        columnHelper.accessor('amount', {
          id: 'amount',
          header: 'Balance / Value',
          meta: {
            minWidth: 125,
          },
          cell: (props) => {
            const { amount, value, collateralId } = props.row.original
            return (
              <YStack>
                <BodyText color={value === 0 ? 'secondary' : 'primary'}>
                  {formatCollateralBalance(Math.max(amount, 0), collateralId, { truncate: true })}
                </BodyText>
                {value !== 0 ? (
                  <BodyText color="secondary">{formatUSD(Math.max(value, 0))}</BodyText>
                ) : null}
              </YStack>
            )
          },
        }),
        columnHelper.accessor('margin', {
          id: 'margin',
          header: 'Margin',
          meta: {
            minWidth: 100,
          },
          cell: (props) => {
            const { margin } = props.row.original

            if (margin < 0) {
              return (
                <TooltipText
                  color="red"
                  label={formatUSD(margin)}
                  tooltipContent={`Your account has a negative ${formatUSD(
                    Math.abs(margin)
                  )} cash balance. Deposit USDC into the account to pay off the account's debt.`}
                />
              )
            }

            return <BodyText color={margin < 0 ? 'red' : 'primary'}>{formatUSD(margin)}</BodyText>
          },
        }),
        showApy
          ? columnHelper.accessor('apy', {
              id: 'apy',
              header: 'APY',
              meta: {
                minWidth: 100,
              },
              cell: (props) => {
                const { collateralId, amount, apy } = props.row.original
                const yieldConfig = collateralConfig[collateralId].yield

                if (!yieldConfig) {
                  return <BodyText color="secondary">—</BodyText>
                }

                if (collateralId === CollateralId.USDC && amount < 0) {
                  return (
                    <TooltipText
                      color="red"
                      label={formatPercentage(-+borrowApy)}
                      tooltipContent="Borrowing USDC against your collateral and open positions."
                    />
                  )
                }

                return (
                  <TooltipText
                    color="green"
                    label={formatCollateralApy(collateralId, apy)}
                    tooltipContent={yieldConfig.copy}
                  />
                )
              },
            })
          : null,
        showActions
          ? columnHelper.accessor('action', {
              id: 'action',
              header: '',
              meta: {
                minWidth: 190,
                maxWidth: 190,
                isActionColumn: true,
              },
              cell: ({
                row: {
                  original: { collateralId, subaccountId },
                },
              }) => (
                <XStack gap="$2">
                  <DepositButton
                    isCta={false}
                    defaultDepositToken={getDefaultDepositTokenForToken(
                      getTokenForCollateral(collateralId)
                    )}
                  />
                  <TransferButton
                    defaultFromSubaccountId={subaccountId}
                    // user is typically transferring to funding
                    defaultToSubaccountId="funding"
                    // lock transfer asset
                    asset={getTokenForCollateral(collateralId)}
                  />
                </XStack>
              ),
            })
          : null,
      ]),
    [columnHelper, showActions, showApy, borrowApy]
  )

  return (
    <Table emptyMessage="No balances" size="lg" {...tableProps} data={rows} columns={columns} />
  )
}

export default BalancesTable
